"use client";

import { useCallback, useState } from "react";
import { useCommandAttempt } from "@/hooks/commands/useCommandAttempt";

/** How the server answered a command, in the terms a UI has to decide on. */
export type CommandOutcome<Data> = {
  kind: "committed";
  data: Data;
}
/** Someone changed the record since it was read: reload, then decide again. */ | {
  kind: "conflict";
  currentRevision: number | null;
  message: string;
}
/** The page holds no usable revision or operation: reload before retrying. */ | {
  kind: "reload";
  message: string;
}
/**
 * The server refused the change as sent. `details` is what the refusal names beside its
 * message (domainErrorResponse): the deals a change costs, the facts it lacks.
 */ | {
  kind: "refused";
  code: string | null;
  message: string;
  details?: Record<string, unknown>;
}
/** No definite answer: retrying the same change is safe and keeps its operation. */ | {
  kind: "unavailable";
  message: string;
};
const UNAVAILABLE = "Die Speicherung konnte nicht bestätigt werden. Bitte versuchen Sie es erneut.";
const REFUSED = "Die Änderung konnte nicht gespeichert werden.";
interface ErrorBody {
  error?: string;
  code?: string;
  currentRevision?: number;
  data?: unknown;
  [named: string]: unknown;
}
export interface CommandCall<Input> {
  /** The command's route, which names its target. */
  url: string;
  input: Input;
  /** The revision the user decided against; null for a creation. */
  revision: number | null;
}

/**
 * Sends one kind of aggregate command: the input plus its envelope
 * (`operationId`, `revision`). A retry after an unavailable answer repeats the
 * same operation; every definite answer settles it.
 */
export function useAggregateCommand<Input extends object, Data>({
  method
}: {
  method: "POST" | "PATCH" | "PUT" | "DELETE";
}) {
  const attempt = useCommandAttempt();
  const [pending, setPending] = useState(false);
  const send = useCallback(async ({
    url,
    input,
    revision
  }: CommandCall<Input>): Promise<CommandOutcome<Data>> => {
    const envelope = attempt.prepare({
      url,
      input
    }, revision);
    setPending(true);
    try {
      const response = await fetch(url, {
        method,
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({
          ...input,
          ...envelope
        })
      });
      const body = (await response.json().catch(() => ({}))) as ErrorBody;
      if (response.status >= 500) return {
        kind: "unavailable",
        message: body.error ?? UNAVAILABLE
      };
      attempt.settle();
      if (response.ok) return {
        kind: "committed",
        data: (body.data ?? body) as Data
      };
      if (body.code === "CONFLICT") return {
        kind: "conflict",
        currentRevision: body.currentRevision ?? null,
        message: body.error ?? REFUSED
      };
      if (body.code === "RELOAD_REQUIRED" || body.code === "OPERATION_REQUIRED") return {
        kind: "reload",
        message: body.error ?? REFUSED
      };
      const {
        error: _error,
        code: _code,
        ...details
      } = body;
      return {
        kind: "refused",
        code: body.code ?? null,
        message: body.error ?? REFUSED,
        ...(Object.keys(details).length > 0 ? {
          details
        } : {})
      };
    } catch {
      return {
        kind: "unavailable",
        message: UNAVAILABLE
      };
    } finally {
      setPending(false);
    }
  }, [attempt, method]);
  return {
    send,
    pending
  };
}